// Slice 4 client: progress over time, QC checkpoints, analysis notes,
// progression. Shapes mirror src/teacher/{progress,checkpoints,annotations}.ts.

import type { Dataset } from './teacher-api';

export type PointStatus = 'confirmed' | 'needs_review' | 'not_assessable' | 'not_yet_confirmed' | 'absent' | 'catchup' | 'exempt';

export interface TestInfo {
  id: string; title: string; criterion: string; year_level: number; sat_on: string | null; fingerprint: string | null;
  finalised: boolean; provisional: boolean;
}
export interface Point {
  status: PointStatus; level: number | null; hollow: boolean; label: string | null;
  class_name_at_sitting: string; class_id_at_sitting: string;
  test: TestInfo; break_before: string | null; trend: 'up' | 'down' | 'same' | null; raw: string | null;
}
export interface StrandCell { mastered: number; n: number; items: number; rate: number | null; note: string | null }
export interface StudentProgress {
  student: { id: string; name: string; student_code: string | null; class_id: string; archived: boolean };
  tests: (TestInfo & { class_name_at_sitting: string; attendance: string })[];
  criteria: {
    criterion: string; points: Point[];
    strand_table: { strands: string[]; tests: { test: TestInfo; cells: Record<string, StrandCell | null> }[] };
  }[];
  feedback_thread: {
    test: { id: string; title: string; sat_on: string | null }; next_step: string | null;
    following: { test: { id: string; title: string; sat_on: string | null }; strength: string | null; improvement: string | null } | null;
  }[];
  recurring_misconceptions: { code: string; meaning: string; test: { id: string; title: string }; also_in: { id: string; title: string }[]; text: string }[];
}

export const BUCKETS = ['0', '1-2', '3-4', '5-6', '7-8', 'not_assessable', 'not_yet_confirmed'] as const;
export type Bucket = (typeof BUCKETS)[number];
export interface StrandRate { label: string; mastered: number; n: number; rate: number | null; items: number; students: number }
export interface Distribution { buckets: Record<Bucket, number>; population: number }
export interface ClassProgress {
  filters: { year: number | null; class: string | null; criterion: string | null; sameCohort: boolean };
  years: number[]; criteria: string[]; classes: { id: string; name: string }[];
  cohort: { on: boolean; students: number };
  tests: ({ test: TestInfo; counts: { enrolled: number; assessed: number; absent: number; catchup: number; exempt: number }; strands: StrandRate[];
    classes: ({ class_name: string; strands: StrandRate[] } & Distribution)[] } & Distribution)[];
  movers: { student: { id: string; name: string }; kind: 'level_fell' | 'recurring_code'; reason: string }[];
}

export type QcStatus = 'pass' | 'fail' | 'not_applicable';
export interface QcRow {
  id: string; label: string; status: QcStatus; detail: string;
  students?: { id: string; name: string }[]; counts?: Record<string, unknown>;
  action?: { kind: 'record_manual_entry'; studentIds: string[] };
}
export interface CheckpointView {
  n: number; passed_by: string; passed_at: string; stale: boolean;
  changed: { reasons: string[]; students: { id: string; name: string }[] }; label: string | null;
}
export interface QcView {
  assessment: { id: string; status: 'active' | 'finalised'; revision: number; finalised_at: string | null; finalised_by: string | null };
  banner: 'provisional' | 'checkpoint_2' | 'finalised';
  prerequisite: string | null;
  qc: { checkpoint: 1 | 2 | 3; passed: boolean; rows: QcRow[] };
  checkpoints: CheckpointView[];
}

export type AnalysisTabId = 'item' | 'strand' | 'topic' | 'command';
export interface NoteView { tab: AnalysisTabId; text: string; revision: number; basis_digest: string; updated_by: string; updated_at: string; stale: string | null }

export const FINALISED_TEXT = 'Finalised; reopen first';

/** §06 banner state from the cached dataset. */
export function bannerOf(ds: Dataset | undefined): 'provisional' | 'checkpoint_2' | 'finalised' | null {
  if (!ds) return null;
  if (ds.assessment.status === 'finalised') return 'finalised';
  return ds.checkpoints?.some((c) => c.n === 2) ? 'checkpoint_2' : 'provisional';
}

export const BUCKET_LABEL: Record<Bucket, string> = {
  '0': 'Level 0', '1-2': '1-2', '3-4': '3-4', '5-6': '5-6', '7-8': '7-8', not_assessable: 'Not assessable', not_yet_confirmed: 'Not yet confirmed',
};

export const TREND_WORD = { up: 'Up', down: 'Down', same: 'Same' } as const;

export function dateOf(d: string | null): string {
  return d ? new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'no date';
}
